<template>
  <v-container class="app-container">
  <div class="commission-view">
    <v-alert
      v-if="showNotifyBanner"
      type="info"
      variant="tonal"
      class="mb-4"
      closable
      @click:close="dismissNotifyBanner"
    >
      <div class="d-flex align-center flex-wrap ga-2">
        <span>Push-Benachrichtigungen für neue Anfragen aktivieren?</span>
        <v-btn size="small" color="primary" variant="flat" @click="enableCommissionNotifications">
          Benachrichtigungen erlauben
        </v-btn>
      </div>
    </v-alert>

    <!-- Offene Aufträge (zur Bearbeitung) -->
    <v-card class="commission-card mb-6" v-if="openOrders.length > 0">
      <div class="card-header active-header">
        <div class="title-container">
          <v-icon color="amber" size="24" class="mr-2"
            >mdi-clock-outline</v-icon
          >
          <h2 class="card-title">Offene Anfragen</h2>
        </div>

        <div class="header-actions">
          <v-chip variant="elevated" color="amber" class="status-counter">
            {{ openOrders.length }}
          </v-chip>
        </div>
      </div>

      <v-divider class="card-divider"></v-divider>

      <div class="card-content">
        <!-- Desktop Auftragsansicht -->
        <div class="order-list" v-if="!isMobile">
          <div
            v-for="order in openOrders"
            :key="order.id"
            class="order-container"
          >
            <div class="order-header" @click="toggleOrderExpand(order.id)">
              <div class="order-info">
                <div class="order-number">
                  <v-icon color="success" size="small" class="mr-1"
                    >mdi-identifier</v-icon
                  >
                  <span class="order-id">{{
                    order.order_number || `Auftrag #${order.id}`
                  }}</span>
                  <v-chip
                    v-if="order.items && order.items.length > 1"
                    size="x-small"
                    color="success"
                    class="ml-2 position-counter"
                  >
                    {{ order.items.length }} Teile
                  </v-chip>
                </div>
                <div class="order-date">
                  <v-icon color="success" size="small" class="mr-1"
                    >mdi-calendar-clock</v-icon
                  >
                  {{ formatDateTime(order.request_datetime) }}
                </div>
                <div
                  class="order-employee"
                  @click.stop="showEmployeeDetails(order)"
                >
                  <v-icon color="success" size="small" class="mr-1"
                    >mdi-account</v-icon
                  >
                  {{ order.mitarbeiternummer || "Unbekannt" }}
                  {{ order.abteilung ? `(${order.abteilung})` : "" }}
                </div>
              </div>

              <div class="order-status">
                <v-chip
                  size="small"
                  :color="getStatusColor(order.status)"
                  class="status-chip mr-2"
                >
                  {{ order.status }}
                </v-chip>

                <div class="action-buttons" v-if="!order.allCompleted">
                  <v-btn
                    icon
                    variant="text"
                    color="success"
                    size="small"
                    class="action-btn"
                    @click.stop="markAllAsCompleted(order)"
                    :loading="updating"
                    :disabled="updating"
                  >
                    <v-icon>mdi-check-all</v-icon>
                  </v-btn>
                </div>

                <v-btn
                  icon
                  variant="text"
                  color="success"
                  size="small"
                  class="action-btn mr-2"
                  @click.stop="printOrderPDF(order)"
                >
                  <v-icon>mdi-printer</v-icon>
                </v-btn>

                <v-icon
                  :class="expandedOrders.has(order.id) ? 'rotate-icon' : ''"
                  class="expand-icon"
                >
                  mdi-chevron-down
                </v-icon>
              </div>
            </div>

            <!-- Ersatzteilliste für den Auftrag -->
            <div
              v-if="expandedOrders.has(order.id)"
              class="order-items-container"
            >
              <table class="items-table">
                <thead>
                  <tr>
                    <th>Ersatzteil</th>
                    <th>Lagerort</th>
                    <th>Menge</th>
                    <th>Anmerkung</th>
                    <th>Lager-Anmerkung</th>
                    <th>Status</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in order.items"
                    :key="item.id"
                    :class="rowClass(item.status)"
                  >
                    <td>{{ item.ersatzteil }}</td>
                    <td>{{ item.lagerort }}</td>
                    <td>{{ item.stueckzahl }}</td>
                    <td>
                      <v-tooltip location="bottom">
                        <template v-slot:activator="{ props }">
                          <span v-bind="props" class="truncate-text">
                            {{ shortenText(item.sonstiges) }}
                          </span>
                        </template>
                        <span>{{ item.sonstiges }}</span>
                      </v-tooltip>
                    </td>
                    <td>
                      <v-text-field
                        v-model="item.commission_comment"
                        density="compact"
                        variant="solo"
                        bg-color="rgba(30, 30, 40, 0.5)"
                        hide-details
                        class="comment-field"
                        placeholder="Anmerkung hinzufügen..."
                        @keydown="handleCommentKeydown($event, item)"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-select
                        v-model="item.status"
                        :items="statusOptions"
                        variant="solo"
                        density="compact"
                        bg-color="rgba(30, 30, 40, 0.5)"
                        hide-details
                        class="status-select"
                        @update:model-value="onStatusChange(item, $event)"
                      ></v-select>
                    </td>
                    <td class="actions-column">
                      <v-btn
                        icon
                        variant="text"
                        color="success"
                        size="x-small"
                        class="save-btn"
                        @click="updateItem(item)"
                        :loading="updating && updatingItemId === item.id"
                        :disabled="updating"
                      >
                        <v-icon>mdi-content-save</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Mobile-Ansicht -->
        <div class="mobile-orders" v-else>
          <div
            v-for="order in openOrders"
            :key="order.id"
            class="order-card-mobile"
            :class="rowClass(order.status)"
          >
            <div class="order-card-header" @click="toggleOrderExpand(order.id)">
              <div class="order-mobile-info">
                <div class="order-id-mobile">
                  {{ order.order_number || `Auftrag #${order.id}` }}
                  <v-chip
                    v-if="order.items && order.items.length > 1"
                    size="x-small"
                    density="compact"
                    color="success"
                    class="ml-1 position-counter-mobile"
                  >
                    {{ order.items.length }}
                  </v-chip>
                </div>
                <div class="order-date-mobile">
                  <v-icon size="x-small" color="success" class="mr-1"
                    >mdi-calendar-clock</v-icon
                  >
                  {{ formatDateTime(order.request_datetime) }}
                </div>
                <div
                  class="order-employee-mobile"
                  @click.stop="showEmployeeDetails(order)"
                >
                  <v-icon size="x-small" color="success" class="mr-1"
                    >mdi-account</v-icon
                  >
                  {{ order.mitarbeiternummer || "Unbekannt" }}
                </div>
              </div>

              <div class="order-status-mobile">
                <v-chip
                  size="x-small"
                  :color="getStatusColor(order.status)"
                  class="status-chip-mobile"
                >
                  {{ order.status }}
                </v-chip>

                <v-icon
                  :class="expandedOrders.has(order.id) ? 'rotate-icon' : ''"
                  class="expand-icon-mobile"
                  size="small"
                >
                  mdi-chevron-down
                </v-icon>
              </div>
            </div>

            <!-- Mobile Ersatzteilliste -->
            <div v-if="expandedOrders.has(order.id)" class="order-items-mobile">
              <div
                v-for="item in order.items"
                :key="item.id"
                class="item-card-mobile"
                :class="rowClass(item.status)"
              >
                <div class="item-card-content">
                  <div class="item-info-row">
                    <v-icon size="x-small" color="success">mdi-cog</v-icon>
                    <span class="info-label">Ersatzteil:</span>
                    <span class="info-value">{{ item.ersatzteil }}</span>
                  </div>

                  <div class="item-info-row">
                    <v-icon size="x-small" color="success"
                      >mdi-map-marker</v-icon
                    >
                    <span class="info-label">Lagerort:</span>
                    <span class="info-value">{{ item.lagerort }}</span>
                  </div>

                  <div class="item-info-row">
                    <v-icon size="x-small" color="success">mdi-numeric</v-icon>
                    <span class="info-label">Menge:</span>
                    <span class="info-value">{{ item.stueckzahl }}</span>
                  </div>

                  <div class="item-info-row" v-if="item.sonstiges">
                    <v-icon size="x-small" color="success"
                      >mdi-text-box-outline</v-icon
                    >
                    <span class="info-label">Anmerkung:</span>
                    <span class="info-value">{{ item.sonstiges }}</span>
                  </div>

                  <div class="item-info-row">
                    <v-icon size="x-small" color="success"
                      >mdi-text-box-edit</v-icon
                    >
                    <span class="info-label">Lager-Anmerkung:</span>
                    <v-text-field
                      v-model="item.commission_comment"
                      density="compact"
                      variant="solo"
                      bg-color="rgba(30, 30, 40, 0.5)"
                      hide-details
                      class="comment-field-mobile"
                      placeholder="Anmerkung hinzufügen..."
                      @keydown="handleCommentKeydown($event, item)"
                    ></v-text-field>
                  </div>

                  <div class="item-status-row">
                    <v-select
                      v-model="item.status"
                      :items="statusOptions"
                      variant="solo"
                      density="compact"
                      bg-color="rgba(30, 30, 40, 0.5)"
                      hide-details
                      class="status-select-mobile"
                      @update:model-value="onStatusChange(item, $event)"
                    ></v-select>

                    <v-btn
                      variant="text"
                      color="success"
                      size="x-small"
                      class="action-btn-mobile"
                      @click="updateItem(item)"
                      :loading="updating && updatingItemId === item.id"
                      :disabled="updating"
                    >
                      <v-icon start size="x-small">mdi-content-save</v-icon>
                      Speichern
                    </v-btn>
                  </div>
                </div>
              </div>

              <div class="order-actions-mobile">
                <v-btn
                  variant="text"
                  color="success"
                  size="small"
                  class="action-btn-mobile"
                  @click.stop="markAllAsCompleted(order)"
                  :loading="updating"
                  :disabled="updating || order.allCompleted"
                >
                  <v-icon start size="small">mdi-check-all</v-icon>
                  Alle erledigt
                </v-btn>

                <v-btn
                  variant="text"
                  color="primary"
                  size="small"
                  class="action-btn-mobile"
                  @click.stop="printOrderPDF(order)"
                >
                  <v-icon start size="small">mdi-printer</v-icon>
                  Drucken
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Erledigte Aufträge -->
    <v-card class="commission-card mb-6" v-if="doneOrders.length > 0">
      <div class="card-header completed-header">
        <div class="title-container">
          <v-icon color="success" size="24" class="mr-2"
            >mdi-check-circle</v-icon
          >
          <h2 class="card-title">Erledigte Anfragen</h2>
        </div>

        <div class="header-actions">
          <v-chip variant="elevated" color="success" class="status-counter">
            {{ doneOrders.length }}
          </v-chip>
        </div>
      </div>

      <v-divider class="card-divider"></v-divider>

      <!-- Desktop Ansicht: Erledigte Aufträge -->
      <div class="order-list" v-if="!isMobile">
        <div
          v-for="order in doneOrders"
          :key="order.id"
          class="order-container"
        >
          <div class="order-header" @click="toggleOrderExpand(order.id)">
            <div class="order-info">
              <div class="order-number">
                <v-icon color="success" size="small" class="mr-1"
                  >mdi-identifier</v-icon
                >
                <span class="order-id">{{
                  order.order_number || `Auftrag #${order.id}`
                }}</span>
                <v-chip
                  v-if="order.items && order.items.length > 1"
                  size="x-small"
                  color="success"
                  class="ml-2 position-counter"
                >
                  {{ order.items.length }} Teile
                </v-chip>
              </div>
              <div class="order-date">
                <v-icon color="success" size="small" class="mr-1"
                  >mdi-calendar-clock</v-icon
                >
                {{ formatDateTime(order.request_datetime) }}
              </div>
              <div
                class="order-employee"
                @click.stop="showEmployeeDetails(order)"
              >
                <v-icon color="success" size="small" class="mr-1"
                  >mdi-account</v-icon
                >
                {{ order.mitarbeiternummer || "Unbekannt" }}
                {{ order.abteilung ? `(${order.abteilung})` : "" }}
              </div>
            </div>

            <div class="order-status">
              <v-chip
                size="small"
                :color="getStatusColor(order.status)"
                class="status-chip mr-2"
              >
                {{ order.status }}
              </v-chip>

              <v-btn
                icon
                variant="text"
                color="success"
                size="small"
                class="action-btn mr-2"
                @click.stop="printOrderPDF(order)"
              >
                <v-icon>mdi-printer</v-icon>
              </v-btn>

              <v-icon
                :class="expandedOrders.has(order.id) ? 'rotate-icon' : ''"
                class="expand-icon"
              >
                mdi-chevron-down
              </v-icon>
            </div>
          </div>

          <!-- Ersatzteilliste für erledigte Aufträge -->
          <div
            v-if="expandedOrders.has(order.id)"
            class="order-items-container"
          >
            <table class="items-table">
              <thead>
                <tr>
                  <th>Ersatzteil</th>
                  <th>Lagerort</th>
                  <th>Menge</th>
                  <th>Anmerkung</th>
                  <th>Lager-Anmerkung</th>
                  <th>Status</th>
                  <th>Bearbeitungsende</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in order.items"
                  :key="item.id"
                  :class="rowClass(item.status)"
                >
                  <td>{{ item.ersatzteil }}</td>
                  <td>{{ item.lagerort }}</td>
                  <td>{{ item.stueckzahl }}</td>
                  <td>
                    <v-tooltip location="bottom">
                      <template v-slot:activator="{ props }">
                        <span v-bind="props" class="truncate-text">
                          {{ shortenText(item.sonstiges) }}
                        </span>
                      </template>
                      <span>{{ item.sonstiges }}</span>
                    </v-tooltip>
                  </td>
                  <td>
                    <v-tooltip location="bottom">
                      <template v-slot:activator="{ props }">
                        <span v-bind="props" class="truncate-text">
                          {{ shortenText(item.commission_comment) }}
                        </span>
                      </template>
                      <span>{{ item.commission_comment }}</span>
                    </v-tooltip>
                  </td>
                  <td>
                    <v-chip
                      size="x-small"
                      :color="getStatusColor(item.status)"
                      class="status-chip"
                    >
                      {{ item.status }}
                    </v-chip>
                  </td>
                  <td>
                    {{ item.ended_at ? formatDateTime(item.ended_at) : "-" }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <!-- Nach der Liste der erledigten Aufträge einfügen (vor dem schließenden </div> des Desktop-Bereichs) -->
        <div
          v-if="totalDoneOrders > doneOrders.length"
          class="show-all-container"
        >
          <v-btn
            color="success"
            variant="tonal"
            size="small"
            class="show-more-btn"
            @click="loadMoreDoneOrders"
            :loading="loadingMoreOrders"
          >
            <v-icon start>mdi-plus</v-icon>
            10 weitere Aufträge laden
          </v-btn>
          <div class="text-caption mt-1">
            {{ totalDoneOrders - doneOrders.length }} weitere erledigte Anfragen
            sind verfügbar
          </div>
        </div>

        <!-- Wenn mehr als die Standardmenge geladen wurde -->
        <div v-if="doneOrdersPage > 1" class="show-less-container">
          <v-btn
            color="grey"
            variant="text"
            size="small"
            class="show-less-btn"
            @click="resetDoneOrders"
          >
            <v-icon start>mdi-arrow-collapse-up</v-icon>
            Weniger anzeigen
          </v-btn>
        </div>

        <!-- "Alle anzeigen" Button -->
        <div
          v-if="
            totalDoneOrders > defaultDoneOrdersLimit && doneOrdersPage === 1
          "
          class="show-all-container"
        >
          <v-btn
            color="success"
            variant="outlined"
            size="small"
            class="show-all-btn"
            @click="loadAllDoneOrders"
            :loading="loadingMoreOrders"
          >
            <v-icon start>mdi-eye</v-icon>
            Alle {{ totalDoneOrders }} erledigten Aufträge anzeigen
          </v-btn>
        </div>
      </div>

      <!-- Mobile Ansicht: Erledigte Aufträge -->
      <div class="mobile-orders" v-else>
        <div
          v-for="order in doneOrders"
          :key="order.id"
          class="order-card-mobile completed"
        >
          <div class="order-card-header" @click="toggleOrderExpand(order.id)">
            <div class="order-mobile-info">
              <div class="order-id-mobile">
                {{ order.order_number || `Auftrag #${order.id}` }}
                <v-chip
                  v-if="order.items && order.items.length > 1"
                  size="x-small"
                  density="compact"
                  color="success"
                  class="ml-1 position-counter-mobile"
                >
                  {{ order.items.length }}
                </v-chip>
              </div>
              <div class="order-date-mobile">
                <v-icon size="x-small" color="success" class="mr-1"
                  >mdi-calendar-clock</v-icon
                >
                {{ formatDateTime(order.request_datetime) }}
              </div>
              <div
                class="order-employee-mobile"
                @click.stop="showEmployeeDetails(order)"
              >
                <v-icon size="x-small" color="success" class="mr-1"
                  >mdi-account</v-icon
                >
                {{ order.mitarbeiternummer || "Unbekannt" }}
              </div>
            </div>

            <div class="order-status-mobile">
              <v-chip
                size="x-small"
                :color="getStatusColor(order.status)"
                class="status-chip-mobile"
              >
                {{ order.status }}
              </v-chip>

              <v-icon
                :class="expandedOrders.has(order.id) ? 'rotate-icon' : ''"
                class="expand-icon-mobile"
                size="small"
              >
                mdi-chevron-down
              </v-icon>
            </div>
          </div>

          <!-- Mobile Erledigte Ersatzteilliste -->
          <div v-if="expandedOrders.has(order.id)" class="order-items-mobile">
            <div
              v-for="item in order.items"
              :key="item.id"
              class="item-card-mobile"
              :class="rowClass(item.status)"
            >
              <div class="item-card-content">
                <div class="item-info-row">
                  <v-icon size="x-small" color="success">mdi-cog</v-icon>
                  <span class="info-label">Ersatzteil:</span>
                  <span class="info-value">{{ item.ersatzteil }}</span>
                </div>

                <div class="item-info-row">
                  <v-icon size="x-small" color="success">mdi-map-marker</v-icon>
                  <span class="info-label">Lagerort:</span>
                  <span class="info-value">{{ item.lagerort }}</span>
                </div>

                <div class="item-info-row">
                  <v-icon size="x-small" color="success">mdi-numeric</v-icon>
                  <span class="info-label">Menge:</span>
                  <span class="info-value">{{ item.stueckzahl }}</span>
                </div>

                <div class="item-info-row" v-if="item.sonstiges">
                  <v-icon size="x-small" color="success"
                    >mdi-text-box-outline</v-icon
                  >
                  <span class="info-label">Anmerkung:</span>
                  <span class="info-value">{{ item.sonstiges }}</span>
                </div>

                <div class="item-info-row" v-if="item.commission_comment">
                  <v-icon size="x-small" color="amber"
                    >mdi-comment-text-outline</v-icon
                  >
                  <span class="info-label">Lager:</span>
                  <span class="info-value">{{ item.commission_comment }}</span>
                </div>

                <div class="item-info-row">
                  <v-icon size="x-small" color="success"
                    >mdi-calendar-check</v-icon
                  >
                  <span class="info-label">Erledigt am:</span>
                  <span class="info-value">{{
                    item.ended_at ? formatDateTime(item.ended_at) : "-"
                  }}</span>
                </div>

                <div class="item-status-row">
                  <v-chip
                    size="x-small"
                    :color="getStatusColor(item.status)"
                    class="status-chip-mobile"
                  >
                    {{ item.status }}
                  </v-chip>
                </div>
              </div>
            </div>

            <div class="order-actions-mobile">
              <v-btn
                variant="text"
                color="primary"
                size="small"
                class="action-btn-mobile"
                @click.stop="printOrderPDF(order)"
              >
                <v-icon start size="small">mdi-printer</v-icon>
                Drucken
              </v-btn>
            </div>
          </div>
        </div>
        <!-- Am Ende der mobilen Ansicht für erledigte Aufträge einfügen (vor dem schließenden </div>) -->
        <div
          v-if="totalDoneOrders > doneOrders.length"
          class="show-all-container"
        >
          <v-btn
            color="success"
            variant="tonal"
            size="small"
            class="show-more-btn"
            @click="loadMoreDoneOrders"
            :loading="loadingMoreOrders"
          >
            <v-icon start>mdi-plus</v-icon>
            10 weitere Aufträge laden
          </v-btn>
          <div class="text-caption mt-1">
            {{ totalDoneOrders - doneOrders.length }} weitere erledigte Anfragen
            sind verfügbar
          </div>
        </div>

        <!-- Wenn mehr als die Standardmenge geladen wurde -->
        <div v-if="doneOrdersPage > 1" class="show-less-container">
          <v-btn
            color="grey"
            variant="text"
            size="small"
            class="show-less-btn"
            @click="resetDoneOrders"
          >
            <v-icon start>mdi-arrow-collapse-up</v-icon>
            Weniger anzeigen
          </v-btn>
        </div>

        <!-- "Alle anzeigen" Button -->
        <div
          v-if="
            totalDoneOrders > defaultDoneOrdersLimit && doneOrdersPage === 1
          "
          class="show-all-container"
        >
          <v-btn
            color="success"
            variant="outlined"
            size="small"
            class="show-all-btn"
            @click="loadAllDoneOrders"
            :loading="loadingMoreOrders"
          >
            <v-icon start>mdi-eye</v-icon>
            Alle {{ totalDoneOrders }} erledigten Aufträge anzeigen
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Keine Anfragen vorhanden -->
    <v-card class="commission-card no-data-card" v-if="orders.length === 0">
      <div class="card-header">
        <div class="title-container">
          <v-icon color="primary" size="24" class="mr-2"
            >mdi-information-outline</v-icon
          >
          <h2 class="card-title">Ersatzteil-Anfragen</h2>
        </div>
      </div>

      <v-divider class="card-divider"></v-divider>

      <div class="no-data-content">
        <v-icon size="64" color="grey">mdi-package-variant-closed</v-icon>
        <h3>Keine Aufträge vorhanden</h3>
        <p>Aktuell liegen keine Ersatzteil-Anfragen zur Bearbeitung vor.</p>
      </div>
    </v-card>
  </div>
</v-container>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import axios from "axios";
import { API_BASE_URL, WS_URL } from "../config";
import { useToast } from "vue-toastification";
import { useAuthStore } from "@/stores/auth";
import { useDisplay } from "vuetify";
import jsPDF from "jspdf";
import CommissionNotificationService from "@/services/CommissionNotificationService";

// ======== Basis-Setup ========
const toast = useToast();
const authStore = useAuthStore();
const { mobile, mdAndDown } = useDisplay();
const isMobile = computed(() => mobile.value || mdAndDown.value);
const showAllDoneOrders = ref(false);
const loadingMoreOrders = ref(false);
const defaultDoneOrdersLimit = 10;
const doneOrdersPage = ref(1);
const totalDoneOrders = computed(
  () => orders.value.filter((o) => o.status.toLowerCase() === "erledigt").length
);

// ======== PDF-Generierung und Druck ========
const printOrderPDF = (order) => {
  try {
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const margin = 20;
    let y = margin;

    pdf.setFont("helvetica", "normal");

    // Titel
    pdf.setFontSize(16);
    pdf.text("Ersatzteil-Anfrage", margin, y);
    y += 10;

    // Basisinformationen
    pdf.setFontSize(12);
    pdf.text(
      `Auftragsnummer: ${order.order_number || `#${order.id}`}`,
      margin,
      y
    );
    y += 8;
    pdf.text(`Datum: ${formatDateTime(order.request_datetime)}`, margin, y);
    y += 8;

    // Mitarbeiterinformationen, falls vorhanden
    if (order.mitarbeiternummer) {
      pdf.text(
        `Mitarbeiter: ${order.mitarbeiternummer} (${order.abteilung || "-"})`,
        margin,
        y
      );
      y += 8;
    }

    // Status-Information
    pdf.text(`Status: ${order.status || "offen"}`, margin, y);
    y += 15;

    // Überschrift für Teile
    pdf.setFontSize(14);
    pdf.text("Ersatzteile:", margin, y);
    y += 10;

    // Tabellenkopf
    pdf.setFontSize(12);
    pdf.text("Bezeichnung", margin, y);
    pdf.text("Lagerort", margin + 70, y);
    pdf.text("Menge", margin + 120, y);
    pdf.text("Status", margin + 145, y);
    y += 2;

    // Trennlinie
    pdf.setDrawColor(0);
    pdf.setLineWidth(0.1);
    pdf.line(margin, y, 190, y);
    y += 8;

    // Einzelteile auflisten
    pdf.setFontSize(11);
    order.items.forEach((item) => {
      if (y > 270) {
        pdf.addPage();
        y = margin;
      }
      pdf.text(item.ersatzteil || "-", margin, y);
      pdf.text(item.lagerort || "-", margin + 70, y);
      pdf.text(item.stueckzahl?.toString() || "1", margin + 120, y);
      pdf.text(item.status || "offen", margin + 145, y);

      if (item.sonstiges) {
        y += 6;
        pdf.setFontSize(10);
        pdf.text("Anmerkung: " + item.sonstiges, margin + 5, y);
        pdf.setFontSize(11);
      }

      y += 10;
    });

    y += 5;
    pdf.line(margin, y, 190, y);
    y += 10;

    // Fußzeile
    pdf.setFontSize(10);
    pdf.text(`Erstellt am: ${formatDateTime(new Date())}`, margin, y);

    // Geräteabhängige Anzeige
    if (isMobile.value) {
      // Mobile: PDF direkt zum Download anbieten
      toast.info("PDF wird heruntergeladen...");
      pdf.save("order.pdf");
    } else {
      // Desktop: Im neuen Tab öffnen
      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");
    }
  } catch (error) {
    console.error("Fehler bei der PDF-Generierung:", error);
    toast.error("Fehler beim Erstellen der PDF-Datei");
  }
};

// ======== Zustandsverwaltung ========
const requests = ref([]);
const orders = ref([]);
const statusOptions = ref(["offen", "wartend", "erledigt", "nicht vorhanden"]);
const updating = ref(false);
const expandedOrders = ref(new Set());
const notifyBannerDismissed = ref(
  localStorage.getItem('commission-notify-banner-dismissed') === '1'
);

const showNotifyBanner = computed(() => {
  if (notifyBannerDismissed.value) return false;
  const perm = CommissionNotificationService.getPermission();
  return perm === 'default';
});

function dismissNotifyBanner() {
  notifyBannerDismissed.value = true;
  localStorage.setItem('commission-notify-banner-dismissed', '1');
}

async function enableCommissionNotifications() {
  const result = await CommissionNotificationService.requestPermission();
  if (result === 'granted') {
    toast.success('Benachrichtigungen aktiviert');
    dismissNotifyBanner();
  } else if (result === 'denied') {
    toast.warning('Benachrichtigungen wurden blockiert (Browser-Einstellungen)');
  } else if (result === 'unsupported') {
    toast.info('Browser unterstützt keine Desktop-Benachrichtigungen');
  }
}

// ======== Berechnete Eigenschaften ========
// Aufteilung in offene und erledigte Aufträge
const openOrders = computed(() =>
  orders.value
    .filter((o) => o.status.toLowerCase() !== "erledigt")
    .sort((a, b) => new Date(b.request_datetime) - new Date(a.request_datetime))
);

const doneOrders = computed(() => {
  // Filter und sortiere wie zuvor
  const filteredOrders = orders.value
    .filter((o) => o.status.toLowerCase() === "erledigt")
    .sort(
      (a, b) => new Date(b.request_datetime) - new Date(a.request_datetime)
    );

  // Wenn "Alle anzeigen" aktiviert ist, zeige alle an,
  // ansonsten nur die ersten X Aufträge basierend auf der aktuellen Seite
  return showAllDoneOrders.value
    ? filteredOrders
    : filteredOrders.slice(0, doneOrdersPage.value * defaultDoneOrdersLimit);
});

// Funktion zum Laden weiterer Aufträge
const loadMoreDoneOrders = async () => {
  loadingMoreOrders.value = true;

  // Kurze Verzögerung für besseres Nutzererlebnis
  await new Promise((resolve) => setTimeout(resolve, 300));

  // Erhöhe die Seite um 1
  doneOrdersPage.value++;

  // Nach dem Laden nach unten scrollen, um die neuen Aufträge zu sehen
  nextTick(() => {
    // Finde das letzte Element vor den neu geladenen Aufträgen
    const lastVisibleIndex =
      (doneOrdersPage.value - 1) * defaultDoneOrdersLimit - 1;
    const targetElement =
      document.querySelectorAll(".order-container")[lastVisibleIndex];

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  });

  loadingMoreOrders.value = false;
};

// Funktion, um zurück zur Standardansicht zu gehen
const resetDoneOrders = () => {
  doneOrdersPage.value = 1;
  showAllDoneOrders.value = false;
};

// Neue Funktion zum Laden aller erledigten Aufträge
const loadAllDoneOrders = () => {
  showAllDoneOrders.value = true;
  loadingMoreOrders.value = true;

  // Warte kurz, um den Ladezustand anzuzeigen
  window.setTimeout(() => {
    loadingMoreOrders.value = false;
  }, 500);
};

// ======== Hilfsfunktionen ========
// Formatieren des Datums: DD.MM.YYYY HH:MM
function formatDateTime(dateValue) {
  if (!dateValue) return "-";
  const date = new Date(dateValue);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
}

// Kürzen von zu langen Texten für die Anzeige
function shortenText(text) {
  if (!text) return "";
  const maxLength = isMobile.value ? 10 : 25;
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
}

// Funktion zur Bestimmung der Zeilenklasse anhand des Status
const rowClass = (status) => {
  if (!status) return "status-offen";
  switch (status.toLowerCase()) {
    case "wartend":
      return "status-wartend";
    case "erledigt":
      return "status-erledigt";
    case "nicht vorhanden":
      return "status-nicht-vorhanden";
    case "offen":
    default:
      return "status-offen";
  }
};

// Status-basierte Farben für Chips und Akzente
const getStatusColor = (status) => {
  if (!status) return "info";
  switch (status.toLowerCase()) {
    case "wartend":
      return "amber";
    case "erledigt":
      return "success";
    case "nicht vorhanden":
      return "error";
    case "offen":
    default:
      return "info";
  }
};

// Ein- und Ausklappen von Aufträgen
const toggleOrderExpand = (orderId) => {
  if (expandedOrders.value.has(orderId)) {
    expandedOrders.value.delete(orderId);
  } else {
    expandedOrders.value.add(orderId);
  }
};

// Mitarbeiterinformationen anzeigen
const showEmployeeDetails = (order) => {
  if (order.vorname || order.nachname) {
    toast.info(`Mitarbeiter: ${order.vorname} ${order.nachname}`);
  } else {
    toast.info("Keine Mitarbeiterdaten vorhanden.");
  }
};

// ======== API-Interaktionen ========
// Anfragen abrufen und nach Aufträgen gruppieren
const fetchRequests = async () => {
  try {
    // Parameter basierend auf Benutzerrolle festlegen
    const params =
      authStore.user?.admin ||
      authStore.user?.super_admin ||
      authStore.user?.developer ||
      // Universelle Prüfung, die mit allen Formaten für lagerist funktioniert
      authStore.user?.lagerist === true ||
      authStore.user?.lagerist === 1 ||
      authStore.user?.lagerist === "1"
        ? { all: 1 }
        : {};

    const res = await axios.get(`${API_BASE_URL}requests_commissions.php`, {
      withCredentials: true,
      params,
    });

    if (res.data.status === "success") {
      requests.value = res.data.requests;

      // API gibt bereits nach Aufträgen gruppierte Daten zurück
      orders.value = res.data.requests.map((order) => {
        // Stellen Sie sicher, dass order.items existiert und ein Array ist
        const items = order.items || [];

        // Sicherstellen, dass jedes Item die richtigen Eigenschaften hat
        const mappedItems = items.map((item) => ({
          id: item.id,
          order_id: order.id,
          ersatzteil: item.ersatzteil || "",
          lagerort: item.lagerort || "",
          stueckzahl: item.stueckzahl || "0",
          sonstiges: item.sonstiges || "",
          commission_comment: item.commission_comment || "",
          status: item.status || "offen",
          ended_at: item.ended_at || null,
        }));

        return {
          id: order.id,
          order_number: order.order_number || "",
          request_datetime: order.request_datetime || new Date().toISOString(),
          mitarbeiternummer: order.mitarbeiternummer || "",
          abteilung: order.abteilung || "",
          vorname: order.vorname || "",
          nachname: order.nachname || "",
          status: order.status || "offen",
          ended_at: order.ended_at || null,
          items: mappedItems,
          // Hilfseigenschaft für die UI
          get allCompleted() {
            return this.items.every(
              (i) => i.status.toLowerCase() === "erledigt"
            );
          },
        };
      });
    }
  } catch (error) {
    console.error("Fehler beim Laden der Aufträge:", error);
    toast.error("Fehler beim Laden der Aufträge");
  }
};

// Status ändern und einzelnes Ersatzteil aktualisieren
const onStatusChange = async (item, newStatus) => {
  item.status = newStatus;

  // Wenn der Status auf "erledigt" gesetzt wird, setzen wir das Ende-Datum
  if (newStatus.toLowerCase() === "erledigt") {
    toast.info("Das Teil wird als erledigt markiert.");
  }

  // Direkt speichern ohne zusätzlichen Klick
  await updateItem(item, item.order_id);
};

// Enter-Taste im Kommentarfeld abfangen
const handleCommentKeydown = async (event, item) => {
  if (event.key === "Enter") {
    event.preventDefault();
    await updateItem(item, item.order_id);
  }
};

// Ersatzteil aktualisieren
const updateItem = async (item) => {
  updating.value = true;
  try {
    await axios.post(
      `${API_BASE_URL}requests_commissions.php`,
      {
        action: "commission_update",
        id: item.id,
        commission_comment: item.commission_comment || "",
        status: item.status,
      },
      { withCredentials: true }
    );

    toast.success("Ersatzteil aktualisiert");

    // Daten neu laden
    await fetchRequests();

    // Broadcast Update an alle Clients
    await broadcastUpdate();
  } catch (error) {
    console.error("Fehler beim Aktualisieren:", error);
    toast.error("Fehler beim Aktualisieren des Ersatzteils");
  } finally {
    updating.value = false;
  }
};

// Alle Ersatzteile eines Auftrags als erledigt markieren
const markAllAsCompleted = async (order) => {
  updating.value = true;
  try {
    const promises = order.items.map((item) => {
      if (item.status.toLowerCase() !== "erledigt") {
        return axios.post(
          `${API_BASE_URL}requests_commissions.php`,
          {
            action: "commission_update",
            id: item.id,
            commission_comment: item.commission_comment || "",
            status: "erledigt",
          },
          { withCredentials: true }
        );
      }
      return Promise.resolve();
    });

    await Promise.all(promises);
    toast.success("Alle Teile als erledigt markiert");

    // Daten neu laden
    await fetchRequests();

    // Broadcast Update an alle Clients
    await broadcastUpdate();
  } catch (error) {
    console.error("Fehler beim Aktualisieren:", error);
    toast.error("Fehler beim Aktualisieren der Ersatzteile");
  } finally {
    updating.value = false;
  }
};

// ======== WebSocket-Integration ========
// Handler für Request-Updates
const handleRequestUpdate = (data) => {
  fetchRequests();
};

// WebSocket-Verbindung einrichten - nutzt zentrale Verbindung
const setupWebSocket = async () => {
  try {
    const WebSocketService = (await import('@/services/WebSocketService')).default;

    // Stelle sicher, dass WebSocket verbunden ist
    WebSocketService.connect();

    // Handler für Request-Updates registrieren
    WebSocketService.addMessageHandler('request_update', handleRequestUpdate);
    WebSocketService.addMessageHandler('update', handleRequestUpdate);
    WebSocketService.addMessageHandler('new_commission_request', handleRequestUpdate);
  } catch (error) {
    console.error("Fehler beim Einrichten des WebSocket-Handlers:", error);
  }
};

// WebSocket-Update an alle Clients broadcasten
const broadcastUpdate = async () => {
  try {
    const WebSocketService = (await import('@/services/WebSocketService')).default;

    // Warte kurz, falls WebSocket gerade connected
    if (!WebSocketService.isConnected.value) {
      // Versuche nochmal connect
      WebSocketService.connect();
      // Warte bis zu 2 Sekunden
      for (let i = 0; i < 20; i++) {
        if (WebSocketService.isConnected.value) break;
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    if (WebSocketService.isConnected.value) {
      // Sende einfaches "update" das der Server an alle weiterleitet
      WebSocketService.send("update");
    }
  } catch (error) {
    console.error("Fehler beim Broadcasting des Updates:", error);
  }
};

// Handler aufräumen
const cleanupWebSocketHandlers = () => {
  import('@/services/WebSocketService').then(({ default: WebSocketService }) => {
    WebSocketService.removeMessageHandler('request_update', handleRequestUpdate);
    WebSocketService.removeMessageHandler('update', handleRequestUpdate);
    WebSocketService.removeMessageHandler('new_commission_request', handleRequestUpdate);
  });
};

// ======== Komponenten-Lifecycle ========
onMounted(() => {
  // Berechtigungsprüfung und Daten laden
  if (
    authStore.user?.admin ||
    authStore.user?.super_admin ||
    authStore.user?.developer ||
    // Universelle Prüfung, die mit allen Formaten funktioniert
    authStore.user?.lagerist === true ||
    authStore.user?.lagerist === 1 ||
    authStore.user?.lagerist === "1"
  ) {
    fetchRequests();
  } else {
    toast.error("Sie haben keine Berechtigung für diese Ansicht.");
  }

  // WebSocket einrichten
  setupWebSocket();
});

onBeforeUnmount(() => {
  cleanupWebSocketHandlers();
});
</script>

<style scoped>
/* ======== Grundlayout und Container ======== */
/* Container mit konsistenter Breite */
.app-container {
  max-width: 1200px !important;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 32px;
}

.commission-view {
  position: relative;
  width: 100%;
}

.commission-card {
  background: rgba(25, 25, 30, 0.85) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* ======== Header-Bereiche ======== */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 24px; /* Leicht vergrößert für bessere Lesbarkeit */
  background: linear-gradient(
    90deg,
    rgba(33, 150, 243, 0.2),
    rgba(33, 150, 243, 0.1),
    transparent
  );
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.active-header {
  background: linear-gradient(
    90deg,
    rgba(255, 193, 7, 0.2),
    rgba(255, 193, 7, 0.1),
    transparent
  );
}

.completed-header {
  background: linear-gradient(
    90deg,
    rgba(76, 175, 80, 0.2),
    rgba(76, 175, 80, 0.1),
    transparent
  );
}

.title-container {
  display: flex;
  align-items: center;
}

.card-title {
  font-size: 20px; /* Größer für bessere Lesbarkeit */
  font-weight: 500;
  margin: 0;
  letter-spacing: 0.3px;
  background: linear-gradient(45deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-counter {
  font-weight: bold;
  padding: 0 8px;
}

.card-divider {
  border-color: rgba(255, 255, 255, 0.06);
  margin: 0;
}

/* ======== Inhaltsbereiche ======== */
.card-content {
  padding: 24px; /* Vergrößert für bessere Lesbarkeit */
}

/* ======== Auftrags-Layout ======== */
.order-list {
  display: flex;
  flex-direction: column;
  gap: 16px; /* Mehr Platz zwischen den Aufträgen */
}

.order-container {
  background: rgba(30, 30, 40, 0.4);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.04);
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.order-container:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  transform: translateY(-2px);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 18px;
  background: rgba(25, 25, 30, 0.7);
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background 0.2s ease;
}

.order-header:hover {
  background: rgba(33, 150, 243, 0.1);
}

.order-info {
  display: flex;
  flex-direction: column;
  gap: 5px; /* Leicht vergrößert */
}

.order-number {
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
}

.order-date,
.order-employee {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
}

.order-employee {
  cursor: pointer;
  transition: color 0.2s ease;
}

.order-employee:hover {
  color: rgba(33, 150, 243, 0.9);
}

.order-status {
  display: flex;
  align-items: center;
  gap: 12px;
}

.expand-icon {
  font-size: 22px;
  transition: transform 0.3s ease;
}

.rotate-icon {
  transform: rotate(180deg);
}

/* ======== Ersatzteil-Tabellen ======== */
.order-items-container {
  padding: 0 18px 18px;
  animation: slideDown 0.3s ease-out forwards;
  overflow: hidden;
}

@keyframes slideDown {
  from {
    max-height: 0;
    opacity: 0;
  }
  to {
    max-height: 1000px;
    opacity: 1;
  }
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  font-size: 14px;
  margin-top: 14px;
}

.items-table th {
  text-align: left;
  padding: 12px 14px;
  background: rgba(20, 20, 25, 0.6);
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  letter-spacing: 0.3px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 13px;
}

.items-table td {
  padding: 12px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.8);
}

.items-table tr:hover td {
  background: rgba(35, 35, 45, 0.5);
}

/* Batch-Aktionen für Aufträge */
.order-batch-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.batch-btn {
  font-size: 13px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.batch-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

/* Kurzdarstellung von Text */
.truncate-text {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  cursor: default;
}

/* Status-Chip */
.status-chip {
  font-size: 12px;
  font-weight: 500;
}

/* Actions Column */
.actions-column {
  width: 120px;
  text-align: center;
}

/* Save Button in Tabellen */
.save-btn {
  margin: 0;
  min-width: unset;
  height: 32px;
  width: 32px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(33, 150, 243, 0.3);
}

/* Formularelemente innerhalb der Tabelle */
.comment-field,
.status-select {
  margin-bottom: 0;
  font-size: 13px;
}

.comment-field :deep(.v-field__input),
.status-select :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.85);
  padding: 6px 12px;
  min-height: unset !important;
}

.comment-field :deep(.v-field__outline),
.status-select :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

.comment-field:hover :deep(.v-field__outline),
.status-select:hover :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.18);
}

/* Status-Klassen für Ersatzteile und Zeilen */
.status-offen {
  background: rgba(33, 150, 243, 0.1);
}

.status-wartend {
  background: rgba(255, 193, 7, 0.1);
}

.status-erledigt {
  background: rgba(76, 175, 80, 0.1);
}

.status-nicht-vorhanden {
  background: rgba(244, 67, 54, 0.1);
}

/* ======== Mobile Auftragsansicht ======== */
.mobile-orders {
  display: flex;
  flex-direction: column;
  gap: 16px; /* Erhöht für bessere Lesbarkeit */
}

.order-card-mobile {
  background: rgba(30, 30, 40, 0.4);
  border-radius: 12px;
  overflow: hidden;
  border-left: 3px solid rgba(33, 150, 243, 0.6);
  transition: all 0.3s ease;
  margin-bottom: 16px; /* Erhöht */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.order-card-mobile:hover {
  transform: translateX(3px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.order-card-mobile.status-offen {
  border-left-color: rgba(33, 150, 243, 0.6);
}

.order-card-mobile.status-wartend {
  border-left-color: rgba(255, 193, 7, 0.6);
}

.order-card-mobile.status-erledigt {
  border-left-color: rgba(76, 175, 80, 0.6);
}

.order-card-mobile.status-nicht-vorhanden {
  border-left-color: rgba(244, 67, 54, 0.6);
}

.order-card-mobile.completed {
  opacity: 0.85;
}

.order-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: rgba(25, 25, 30, 0.5);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
}

.order-mobile-info {
  display: flex;
  flex-direction: column;
  gap: 4px; /* Erhöht */
}

.order-id-mobile {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
}

.order-date-mobile {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
}

.order-employee-mobile {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  cursor: pointer;
}

.order-status-mobile {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-chip-mobile {
  font-size: 10px;
  font-weight: 500;
}

.expand-icon-mobile {
  font-size: 18px;
  transition: transform 0.3s ease;
}

/* Mobile Ersatzteilliste */
.order-items-mobile {
  padding: 14px 16px;
  background: rgba(20, 20, 25, 0.3);
  animation: slideDown 0.3s ease-out forwards;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.item-card-mobile {
  background: rgba(30, 30, 40, 0.5);
  border-radius: 8px;
  margin-bottom: 14px; /* Erhöht */
  overflow: hidden;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.item-card-mobile:last-child {
  margin-bottom: 8px;
}

.item-card-content {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px; /* Erhöht für bessere Lesbarkeit */
}

.item-info-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-weight: 500;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  min-width: 80px;
}

.info-value {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  word-break: break-word;
}

.item-actions {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
}

/* Formularfeld spezifisch für Mobile */
.comment-field-mobile {
  margin-top: 4px;
  width: 100%;
}

.comment-field-mobile :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  padding: 6px 12px;
}

.status-select-mobile {
  margin-top: 6px;
  margin-bottom: 6px;
}

.status-select-mobile :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  padding: 6px 12px;
}

.item-status-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
}

.action-btn-mobile {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  letter-spacing: 0.3px;
  transition: all 0.3s ease;
  margin-top: 4px;
}

.action-btn-mobile:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

/* Mobile Batch-Aktionen */
.order-actions-mobile {
  display: flex;
  justify-content: space-between; /* Geändert für bessere Verteilung */
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

/* ======== Keine Daten Anzeige ======== */
.no-data-card {
  background: rgba(25, 25, 30, 0.7) !important;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 20px; /* Erhöht für bessere Sichtbarkeit */
  text-align: center;
}

.no-data-content h3 {
  margin: 20px 0 10px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  font-size: 18px;
}

.no-data-content p {
  color: rgba(255, 255, 255, 0.5);
  max-width: 400px;
  font-size: 14px;
}

/* ======== Animationen ======== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.order-container,
.order-card-mobile {
  animation: fadeIn 0.3s ease-out;
}

.order-container:nth-child(2),
.order-card-mobile:nth-child(2) {
  animation-delay: 0.05s;
}
.order-container:nth-child(3),
.order-card-mobile:nth-child(3) {
  animation-delay: 0.1s;
}
.order-container:nth-child(4),
.order-card-mobile:nth-child(4) {
  animation-delay: 0.15s;
}
.order-container:nth-child(5),
.order-card-mobile:nth-child(5) {
  animation-delay: 0.2s;
}

/* ======== Responsive Design ======== */
@media (max-width: 960px) {
  .commission-view {
    padding-top: 20px; /* Kleinerer Abstand auf mittleren Geräten */
  }

  .card-header {
    padding: 16px 20px;
  }

  .card-title {
    font-size: 18px;
  }

  .card-content {
    padding: 20px;
  }

  .items-table th,
  .items-table td {
    padding: 10px 8px;
    font-size: 13px;
  }

  .actions-column {
    width: 80px;
  }

  .truncate-text {
    max-width: 120px;
  }

  .order-batch-actions {
    margin-top: 12px;
  }
}

@media (max-width: 600px) {
  .commission-view {
    padding-top: 16px; /* Kleinerer Abstand auf mobilen Geräten */
  }

  .card-header {
    padding: 14px 16px;
  }

  .card-title {
    font-size: 16px;
  }

  .card-content {
    padding: 12px;
  }

  .info-label {
    min-width: 70px;
    font-size: 12px;
  }

  .info-value {
    font-size: 13px;
  }

  .order-items-mobile {
    padding: 12px;
  }

  .item-card-content {
    padding: 10px;
  }

  .order-id-mobile {
    font-size: 13px;
  }

  .order-date-mobile,
  .order-employee-mobile {
    font-size: 11px;
  }
}

/* Position Counter Styles */
.position-counter {
  font-size: 10px;
  font-weight: 500;
}

.position-counter-mobile {
  font-size: 9px;
  font-weight: 500;
  height: 16px !important;
  min-width: 16px !important;
}

/* Druckbutton Stile */
.print-btn {
  min-width: 32px !important;
  height: 28px !important;
  opacity: 0.8;
  transition: all 0.2s ease;
}

.print-btn:hover {
  opacity: 1;
  transform: translateY(-2px);
}

.print-btn-mobile {
  min-width: 24px !important;
  height: 24px !important;
  opacity: 0.8;
  transition: all 0.2s ease;
}

.print-btn-mobile:hover {
  opacity: 1;
  transform: translateY(-2px);
}

/* Action Buttons */
.action-btn {
  opacity: 0.8;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.action-btn:hover {
  opacity: 1;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

/* Diese Styles am Ende des Styleteils hinzufügen */
.show-all-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin-top: 8px;
}

.show-more-btn,
.show-all-btn {
  min-width: 220px;
  transition: all 0.3s ease;
}

.show-more-btn:hover,
.show-all-btn:hover {
  transform: translateY(-2px);
}

.show-less-container {
  display: flex;
  justify-content: center;
  margin-top: 8px;
  padding-top: 8px;
}

.show-less-btn {
  opacity: 0.7;
  transition: all 0.3s ease;
}

.show-less-btn:hover {
  opacity: 1;
  transform: translateY(-2px);
}

.text-caption {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
}
</style>
